import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Mail, CheckCircle2, AlertCircle, MinusCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" }) + " CT";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    document.title = "Admin | St. John Accounting & Tax";
    return () => m.remove();
  }, []);
}

function LoginForm({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", form);
      onLogin(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="max-w-sm mx-auto bg-white border border-navy-100 rounded-2xl p-8 space-y-4">
      <h1 className="font-serif-display text-3xl text-navy-800">Admin sign in</h1>
      <div>
        <label htmlFor="admin-email" className="block text-[13px] text-navy-600 mb-1">Email</label>
        <input id="admin-email" type="email" required autoComplete="username" data-testid="admin-login-email"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full rounded-lg border border-navy-200 px-3 py-2" />
      </div>
      <div>
        <label htmlFor="admin-password" className="block text-[13px] text-navy-600 mb-1">Password</label>
        <input id="admin-password" type="password" required autoComplete="current-password" data-testid="admin-login-password"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full rounded-lg border border-navy-200 px-3 py-2" />
      </div>
      {error && <p role="alert" data-testid="admin-login-error" className="text-[14px] text-red-700">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-primary w-full justify-center">
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

const STATUS = {
  sent: { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50", label: "Emailed" },
  failed: { icon: AlertCircle, cls: "text-red-700 bg-red-50", label: "Email failed" },
  skipped: { icon: MinusCircle, cls: "text-slate-600 bg-slate-100", label: "Not emailed" },
};

function StatusBadge({ s }) {
  const st = STATUS[s.email_status] || { icon: MinusCircle, cls: "text-slate-600 bg-slate-100", label: "Pending" };
  return (
    <span title={s.email_error || ""} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] ${st.cls}`}>
      <st.icon size={12} /> {st.label}
    </span>
  );
}

function Submissions({ data }) {
  if (!data.total) return <p data-testid="admin-empty" className="mt-8 text-navy-600">No contact form submissions yet.</p>;
  return (
    <ul className="mt-8 space-y-4" data-testid="admin-submissions-list">
      {data.submissions.map((s) => (
        <li key={s.id} data-testid={`admin-submission-${s.id}`} className="bg-white border border-navy-100 rounded-2xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-navy-800">{s.name} <span className="font-normal text-slate-500">· {s.subject}</span></div>
            <div className="flex items-center gap-3 text-[13px] text-slate-600">
              <time dateTime={s.created_at}>{fmt(s.created_at)}</time>
              <StatusBadge s={s} />
            </div>
          </div>
          <div className="mt-1 text-[14px] text-slate-600 flex flex-wrap gap-x-4">
            <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-navy-800"><Mail size={13} />{s.email}</a>
            {s.phone && <span>{s.phone}</span>}
          </div>
          <p className="mt-3 text-[15px] text-navy-700 whitespace-pre-wrap">{s.message}</p>
        </li>
      ))}
    </ul>
  );
}

export default function Admin() {
  useNoIndex();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    http.get("/admin/submissions").then((r) => setData(r.data)).catch((e) => {
      if (e?.response?.status === 401) setUser(false);
      else setError(errText(e));
    });
  }, [user]);

  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
    setData(null);
  };

  return (
    <main data-testid="page-admin" className="container-narrow py-16 min-h-[60vh]">
      {user === null && <p className="text-navy-600">Loading…</p>}
      {user === false && <LoginForm onLogin={setUser} />}
      {user && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-serif-display text-4xl text-navy-800">Contact submissions</h1>
              <p className="mt-2 text-navy-600" data-testid="admin-total">
                {data ? `Contacted ${data.total} time${data.total === 1 ? "" : "s"}` : "Loading…"}
              </p>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-[14px] text-navy-700 hover:text-navy-900">
              <LogOut size={16} /> Sign out
            </button>
          </div>
          {error && <p role="alert" className="mt-6 text-red-700">{error}</p>}
          {data && <Submissions data={data} />}
        </>
      )}
    </main>
  );
}
